import { Component } from '@theme/component';

/**
 * Query-string name for each filter group. Kept short and readable, since
 * these URLs are written by hand and pasted into emails.
 * @type {Record<string, string>}
 */
const FILTER_PARAMS = {
  category: 'category',
  location: 'location',
  audience: 'audience',
  eventType: 'type',
};

/**
 * Client-side filtering for the Events programme: any number of
 * Category/Location/Audience/Event type checkbox filters (OR within a
 * group, AND across groups). Every event is already server-rendered and
 * grouped under its day heading - this only toggles `hidden`, and hides a
 * day's heading too once every event under it is filtered out. Every
 * filter dropdown closes on an outside click, and selected values also
 * show as removable chips below the filter row.
 *
 * Deep links: every event carries its metaobject handle as its id, so
 * /pages/schedule#<handle> lands on that event with its description already
 * open. Each event's title links to that URL, so clicking it puts the
 * shareable link in the address bar.
 *
 * Filter links: the selected filters are mirrored into the query string
 * (e.g. ?type=Supper+Club), so a filtered view can be shared by copying the
 * address bar, and opening that URL arrives with the same filters applied.
 *
 * @extends {Component<{list: HTMLElement, emptyState: HTMLElement, activeFilters: HTMLElement, filterDetails?: HTMLDetailsElement[]}>}
 */
class EventIndexComponent extends Component {
  requiredRefs = ['list', 'emptyState', 'activeFilters'];

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('click', this.#handleClick);
    this.addEventListener('change', this.#handleChange);
    document.addEventListener('click', this.#handleDocumentClick);
    window.addEventListener('hashchange', this.#revealLinkedEvent);
    this.#restoreFiltersFromUrl();
    this.#revealLinkedEvent();
  }

  /**
   * Ticks the checkboxes named in the query string and applies them. Also
   * overrides any checkbox state the browser restored on back/forward, so
   * the page always matches its URL. Values match case-insensitively,
   * since a hand-typed ?type=supper+club should still work.
   */
  #restoreFiltersFromUrl() {
    const params = new URLSearchParams(window.location.search);
    let anyChecked = false;

    for (const checkbox of this.querySelectorAll('input[data-filter-group]')) {
      if (!(checkbox instanceof HTMLInputElement)) continue;
      const param = FILTER_PARAMS[checkbox.dataset.filterGroup ?? ''];
      if (!param) continue;

      const wanted = params.getAll(param).map((value) => value.toLowerCase());
      checkbox.checked = wanted.includes(checkbox.value.toLowerCase());
      if (checkbox.checked) anyChecked = true;
    }

    if (anyChecked) this.#applyFilters();
  }

  /**
   * Rewrites the filter params to match the current selection. Other params
   * (e.g. Shopify's preview_theme_id) and the #event hash are left alone.
   * replaceState, not pushState, so ticking filters doesn't fill the
   * back button with history entries.
   * @param {Record<string, string[]>} selected
   */
  #writeFiltersToUrl(selected) {
    const url = new URL(window.location.href);
    for (const param of Object.values(FILTER_PARAMS)) url.searchParams.delete(param);

    for (const [group, values] of Object.entries(selected)) {
      const param = FILTER_PARAMS[group];
      if (!param) continue;
      for (const value of values) url.searchParams.append(param, value);
    }

    if (url.href !== window.location.href) history.replaceState(history.state, '', url);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('change', this.#handleChange);
    document.removeEventListener('click', this.#handleDocumentClick);
    window.removeEventListener('hashchange', this.#revealLinkedEvent);
  }

  /**
   * Scrolls to the event named in the URL hash and opens its description.
   * Runs on load and on every hash change, so a second shared link opened
   * in the same tab works too. A hash that names no event is left alone.
   */
  #revealLinkedEvent = async () => {
    const handle = decodeURIComponent(window.location.hash.slice(1));
    if (!handle) return;

    const eventEl = this.refs.list.querySelector(`.fairplay-event-index__event[id="${CSS.escape(handle)}"]`);
    if (!(eventEl instanceof HTMLElement)) return;

    // Only possible if the browser restored checkbox state on a back/forward
    // navigation - the linked event must never arrive filtered out.
    if (eventEl.hidden) this.#clearFilters();

    // read-more.js loads separately and may not have upgraded the element
    // yet; expanding before it has would be undone by its own first render.
    const description = eventEl.querySelector('read-more-text');
    if (description) {
      await customElements.whenDefined('read-more-text');
      if (!description.classList.contains('is-expanded') && 'toggleExpanded' in description) {
        /** @type {{toggleExpanded: () => void}} */ (description).toggleExpanded();
      }
    }

    eventEl.scrollIntoView({ block: 'start' });

    eventEl.classList.remove('is-linked');
    // Forces a reflow so the highlight animation restarts on a repeat link.
    void eventEl.offsetWidth;
    eventEl.classList.add('is-linked');
  };

  #clearFilters() {
    for (const checkbox of this.querySelectorAll('input[data-filter-group]')) {
      if (checkbox instanceof HTMLInputElement) checkbox.checked = false;
    }
    this.#applyFilters();
  }

  /**
   * Closes any open filter dropdown when a click lands outside of it -
   * native <details> only closes on re-clicking its own summary otherwise.
   * @param {MouseEvent} event
   */
  #handleDocumentClick = (event) => {
    for (const details of this.refs.filterDetails ?? []) {
      if (details.open && event.target instanceof Node && !details.contains(event.target)) {
        details.removeAttribute('open');
      }
    }
  };

  /** @param {MouseEvent} event */
  #handleClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);

    // Re-clicking the title of the event already in the URL fires no
    // hashchange, so reveal it directly rather than doing nothing.
    const titleLink = target.closest('.fairplay-event-index__event-title-link');
    if (titleLink instanceof HTMLAnchorElement && titleLink.hash === window.location.hash) {
      this.#revealLinkedEvent();
      return;
    }

    const chip = target.closest('.fairplay-event-index__active-filter');
    if (chip instanceof HTMLElement) {
      const { filterGroup, filterValue } = chip.dataset;
      for (const checkbox of this.querySelectorAll('input[data-filter-group]')) {
        if (
          checkbox instanceof HTMLInputElement &&
          checkbox.dataset.filterGroup === filterGroup &&
          checkbox.value === filterValue
        ) {
          checkbox.checked = false;
        }
      }
      this.#applyFilters();
    }
  };

  /** @param {Event} event */
  #handleChange = (event) => {
    if (event.target instanceof HTMLInputElement && event.target.dataset.filterGroup) {
      this.#applyFilters();
    }
  };

  /** @returns {Record<string, string[]>} */
  #selectedFilters() {
    /** @type {Record<string, string[]>} */
    const selected = {};

    for (const details of this.refs.filterDetails ?? []) {
      for (const checkbox of details.querySelectorAll('input[type="checkbox"]')) {
        if (checkbox instanceof HTMLInputElement && checkbox.checked && checkbox.dataset.filterGroup) {
          const group = checkbox.dataset.filterGroup;
          selected[group] ??= [];
          selected[group].push(checkbox.value);
        }
      }
    }

    return selected;
  }

  #applyFilters() {
    const selected = this.#selectedFilters();
    const dayGroups = this.refs.list.querySelectorAll('.fairplay-event-index__day-group');
    let visibleCount = 0;

    for (const dayGroup of dayGroups) {
      if (!(dayGroup instanceof HTMLElement)) continue;

      const events = dayGroup.querySelectorAll('.fairplay-event-index__event');
      let visibleInDay = 0;

      for (const eventEl of events) {
        if (!(eventEl instanceof HTMLElement)) continue;

        const matches = Object.entries(selected).every(([group, values]) => {
          if (values.length === 0) return true;
          return values.includes(eventEl.dataset[group] ?? '');
        });

        eventEl.hidden = !matches;
        if (matches) visibleInDay += 1;
      }

      dayGroup.hidden = visibleInDay === 0;
      visibleCount += visibleInDay;
    }

    this.refs.emptyState.hidden = visibleCount > 0;
    this.#renderActiveFilters(selected);
    this.#writeFiltersToUrl(selected);
  }

  /**
   * Renders one removable chip per currently-selected filter value below
   * the filter row.
   * @param {Record<string, string[]>} selected
   */
  #renderActiveFilters(selected) {
    const container = this.refs.activeFilters;
    container.replaceChildren();

    for (const [group, values] of Object.entries(selected)) {
      for (const value of values) {
        const chip = document.createElement('button');
        chip.type = 'button';
        chip.className = 'fairplay-event-index__active-filter';
        chip.dataset.filterGroup = group;
        chip.dataset.filterValue = value;
        chip.setAttribute('aria-label', `Remove ${value} filter`);

        const label = document.createElement('span');
        label.textContent = value;

        const remove = document.createElement('span');
        remove.className = 'fairplay-event-index__active-filter-remove';
        remove.setAttribute('aria-hidden', 'true');
        remove.textContent = '×';

        chip.append(label, remove);
        container.append(chip);
      }
    }

    container.hidden = container.childElementCount === 0;
  }
}

if (!customElements.get('event-index-component')) {
  customElements.define('event-index-component', EventIndexComponent);
}
